import type { BlockTable, Table } from 'types/block-table';
import type { Page } from 'types/page';
import React from 'react';
import Image from 'next/image';
import { Wrap, Button } from '@leafwell/components';
import HeadingBody from './heading-body';
import WysiwygContent from './wysiwyg-content';

type TableBlockProps = BlockTable & {
  image: Page['acf']['stateProductImage'];
};

function getRowItemClasses({ valueCheck, valueText }) {
  if (valueCheck) {
    return 'min-w-[80px] px-5';
  }
  if (valueText) {
    return 'min-w-[120px] px-2 lg:px-5 !py-3 lg:!py-4 text-sm sm:text-base';
  }
  return 'min-w-[80px]';
}

const Table: React.FC<Table & { sectionId?: string }> = ({
  columns,
  sectionId,
}) => {
  const tableHeader = [];
  const tableValues = [];
  columns.forEach(({ label, values }) => {
    tableHeader.push(label);
    tableValues.push(values);
  });
  const tableRows = Array.from(
    { length: Math.max(...tableValues.map(a => a.length)) },
    (_, i) =>
      tableValues.map((a, index) => ({
        ...a[i],
        className: `${getRowItemClasses(a[i])} ${
          index > 0 ? 'border-l border-solid border-slate-300 text-center' : ''
        }`,
      })),
  );

  return (
    <>
      <div
        className={[
          'w-full mb-8 pb-4 overflow-x-auto leafwell-purple-scrollbar',
        ].join(' ')}
      >
        <table className="w-full ">
          <thead className="bg-gray-100">
            <tr>
              {tableHeader.map((el, thIndex) => {
                return (
                  <th
                    key={`th-${sectionId}-${thIndex}`}
                    className={[
                      'pt-2 px-2 lg:px-5',
                      'text-sm sm:text-base font-medium',
                      thIndex === 0 ? 'text-left' : '',
                    ].join(' ')}
                  >
                    {' '}
                    {el ? el : <span className="opacity-0">label</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, trIndex) => {
              return (
                <tr
                  key={`tr-${sectionId}-${trIndex}`}
                  className={`${
                    trIndex > 0 ? 'border-t border-solid border-slate-300' : ''
                  }`}
                >
                  {row.map((item, tdIndex) => {
                    return (
                      <td
                        key={`td-${sectionId}-${trIndex}-${tdIndex}`}
                        className={item.className}
                      >
                        {item.valueText ? item.valueText : ''}
                        {item.valueCheck ? (
                          <Image
                            alt="check"
                            className="mx-auto"
                            height={30}
                            width={30}
                            src="/check-green.svg"
                          />
                        ) : tdIndex > 0 && !item.valueText ? (
                          <Image
                            alt="empty"
                            className="mx-auto"
                            height={30}
                            width={30}
                            src="/alert-circle.svg"
                          />
                        ) : (
                          ''
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

const TableBlock: React.FC<TableBlockProps> = ({
  sectionId,
  title,
  titleTag,
  introText,
  table,
  button,
  displayProductStateImage,
  image,
}) => {
  return (
    <Wrap className="!block gap-y-8" id={sectionId}>
      <div className="mb-8">
        {title?.length > 0 ? (
          <HeadingBody
            level="2"
            tagName={titleTag as keyof JSX.IntrinsicElements}
            title={title}
            className="!mb-2"
          />
        ) : null}
        {introText?.length > 0 ? <WysiwygContent content={introText} /> : null}
      </div>
      <div className="md:grid gap-14 grid-cols-3 items-start">
        {displayProductStateImage && image ? (
          <div className="flex items-end mb-6 md:mb-0 col-span-1 bg-lilac-50 rounded-md">
            <Image
              alt={image.alt}
              className="mx-auto"
              height={400}
              width={400}
              sizes="(max-width: 767px) 66vw, 33vw"
              src={image.src}
            />
          </div>
        ) : null}
        {table.columns.length > 0 ? (
          <div
            className={
              'flex flex-col justify-between items-start col-span-3 sm:col-span-2'
            }
          >
            <Table columns={table.columns} sectionId={sectionId} />
            {button ? (
              <Button
                href={button.url}
                label={button.title}
                rounded={true}
                target={button.target}
                variant="secondary"
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </Wrap>
  );
};

export default TableBlock;
